import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  MessageCircle, 
  Send, 
  Sparkles, 
  TrendingUp, 
  BarChart3, 
  Shield, 
  Target,
  X,
  Minimize2,
  Maximize2
} from 'lucide-react';
import apiService from '../services/apiService';

const AIChatInterface = ({ isOpen, onClose, onMinimize, isMinimized }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Conversation limits to prevent abuse
  const MAX_MESSAGES_PER_SESSION = 20; // 10 exchanges (user + AI)
  const MAX_MESSAGE_LENGTH = 500; // characters per message
  
  const messageCount = messages.filter(m => m.type === 'user').length;

  // Load conversation history from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('ai_chat_messages');
    const savedSessionId = localStorage.getItem('ai_chat_session_id');
    
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages);
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    }
    
    if (savedSessionId) {
      setSessionId(savedSessionId);
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('ai_chat_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // Save sessionId to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('ai_chat_session_id', sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) {
      loadInvestmentPrompts();
      if (!sessionId) {
        createNewSession();
      }
      
      // Add welcome message
      if (messages.length === 0) {
        setMessages([{
          id: 1,
          type: 'ai',
          content: "Hello! I'm your AI investment copilot. I can help you analyze markets, assess risks, and make informed investment decisions across crypto, equities, and commodities. How can I assist you today?",
          timestamp: new Date()
        }]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const createNewSession = async () => {
    try {
      const newSessionId = await apiService.createAISession();
      setSessionId(newSessionId);
    } catch (error) {
      console.error('Error creating AI session:', error);
    }
  };

  const loadInvestmentPrompts = async () => {
    try {
      const promptsData = await apiService.getInvestmentPrompts();
      setPrompts(promptsData);
    } catch (error) {
      console.error('Error loading investment prompts:', error);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || inputMessage.trim();
    if (!text || loading) return;
    
    // Enforce message limits
    if (messageCount >= MAX_MESSAGES_PER_SESSION) {
      alert(`You've reached the maximum of ${MAX_MESSAGES_PER_SESSION / 2} questions per session. Please start a new conversation.`);
      return;
    }
    
    if (text.length > MAX_MESSAGE_LENGTH) {
      alert(`Message too long! Please keep your question under ${MAX_MESSAGE_LENGTH} characters. Current: ${text.length}`);
      return;
    }

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // Ensure we have a session
      if (!sessionId) {

        await createNewSession();
      }
      

      const response = await apiService.chatWithAI(text, sessionId);
      
      // Add AI response
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(response.timestamp)
      };

      setMessages(prev => [...prev, aiMessage]);

      // Update session ID if new one was created
      if (response.session_id !== sessionId) {
        setSessionId(response.session_id);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm experiencing technical difficulties. Please try again later.",
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getPromptIcon = (category) => {
    switch (category) {
      case 'portfolio': return <BarChart3 className="w-4 h-4" />;
      case 'sentiment': return <TrendingUp className="w-4 h-4" />;
      case 'risk': return <Shield className="w-4 h-4" />;
      case 'opportunities': return <Target className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed ${isMinimized ? 'bottom-4 right-4' : 'inset-4'} z-50 transition-all duration-300`}>
      <Card className={`${isMinimized ? 'w-80 h-16' : 'w-full h-full max-w-4xl mx-auto'} bg-white border-gray-200 shadow-2xl flex flex-col`}>
        {/* Header */}
        <CardHeader className="flex-shrink-0 pb-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r bg-bitcoin-orange rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg text-navy font-heading">AI Assistant</CardTitle>
                {!isMinimized && (
                  <p className="text-sm text-gray-500">Investment Copilot</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!isMinimized && (
                <Button
                  size="sm" 
                  variant="ghost"
                  onClick={onMinimize}
                  className="p-2 hover:bg-gray-100"
                >
                  <Minimize2 className="w-4 h-4" />
                </Button>
              )}
              {isMinimized && (
                <Button
                  size="sm" 
                  variant="ghost"
                  onClick={onMinimize}
                  className="p-2 hover:bg-gray-100"
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm" 
                variant="ghost"
                onClick={onClose}
                className="p-2 hover:bg-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {!isMinimized && (
          <>
            {/* Messages Area */}
            <CardContent className="flex-1 overflow-hidden p-0">
              <div className="h-full flex">
                {/* Chat Messages */}
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            message.type === 'user'
                              ? 'bg-gradient-to-r bg-bitcoin-orange text-white'
                              : message.isError
                              ? 'bg-red-50 border border-red-200 text-red-700'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.content}
                          </div>
                          <div className={`text-xs mt-2 opacity-70 ${
                            message.type === 'user' ? 'text-white' : 'text-gray-500'
                          }`}>
                            {formatTimestamp(message.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                            <span className="text-xs text-gray-500">AI is thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area */}
                  <div className="border-t border-gray-200 p-4">
                    {/* Message Counter */}
                    {messageCount > 0 && (
                      <div className="mb-2 text-xs text-gray-500 text-center">
                        {messageCount} / {MAX_MESSAGES_PER_SESSION / 2} questions used
                        {messageCount >= MAX_MESSAGES_PER_SESSION * 0.8 && (
                          <span className="text-orange-600 font-medium ml-2">
                            ⚠️ Close to limit
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Disclaimer */}
                    <div className="mb-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800">
                        ⚠️ <strong>Disclaimer:</strong> This AI co-pilot provides educational information and general market insights only. It is not investment advice, financial advice, or a recommendation to buy or sell any securities. Always conduct your own research and consult with a qualified financial advisor before making investment decisions.
                      </p>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Input
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask me about investments, markets, or risk analysis..."
                        className="flex-1"
                        disabled={loading || messageCount >= MAX_MESSAGES_PER_SESSION}
                        maxLength={MAX_MESSAGE_LENGTH}
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={loading || !inputMessage.trim() || messageCount >= MAX_MESSAGES_PER_SESSION}
                        className="bg-bitcoin-orange hover:bg-orange-600"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Character counter */}
                    {inputMessage.length > MAX_MESSAGE_LENGTH * 0.8 && (
                      <div className="mt-1 text-xs text-right text-gray-500">
                        {inputMessage.length} / {MAX_MESSAGE_LENGTH} characters
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar with Prompts */}
                <div className="w-80 border-l border-gray-200 bg-gray-50 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h3>
                      <div className="space-y-2">
                        {prompts.map((prompt, index) => (
                          <Button
                            key={prompt || index}
                            variant="outline"
                            size="sm"
                            onClick={() => sendMessage(prompt.prompt)}
                            className="w-full justify-start text-left h-auto min-h-[100px] p-4 border-gray-300 hover:bg-white hover:border-orange-400"
                            disabled={loading}
                          >
                            <div className="flex items-start space-x-3 w-full">
                              <div className="text-bitcoin-orange mt-0.5 flex-shrink-0">
                                {getPromptIcon(prompt.category)}
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="font-medium text-gray-900 text-xs mb-1 whitespace-normal">
                                  {prompt.title}
                                </div>
                                <div className="text-xs text-gray-600 leading-relaxed whitespace-normal break-words">
                                  {prompt.prompt}
                                </div>
                              </div>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <Badge variant="secondary" className="bg-orange-100 text-orange-700 text-xs">
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI-Powered Analysis
                      </Badge>
                      <p className="text-xs text-gray-600 mt-2">
                        Get real-time insights powered by AI with live market data integration.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default AIChatInterface;
